import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Routes, Route, Navigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ApparelHeader from "@/components/apparel/ApparelHeader";
import ApparelShop from "./ApparelShop";
import ApparelManagement from "./ApparelManagement";
import ApparelOrders from "./ApparelOrders";
import { useApparelShop } from "@/hooks/useApparelShop";
import { motion, AnimatePresence } from "framer-motion";

const tabVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

const Apparel: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminRoute = location.pathname.includes('/admin');
  const [activeTab, setActiveTab] = useState<string>("shop");
  const { cartItemCount } = useApparelShop();

  // Function to determine the correct navigation path based on current route
  const getNavigationPath = (path: string) => {
    return isAdminRoute ? `/admin/${path}` : `/${path}`;
  };

  // Set active tab based on the current route
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/apparel/orders')) {
      setActiveTab('orders');
    } else if (path.includes('/apparel/manage')) {
      setActiveTab('manage');
    } else {
      setActiveTab('shop');
    }
  }, [location.pathname]);

  const handleTabNavigation = (path: string) => {
    const targetPath = getNavigationPath(path);
    if (location.pathname !== targetPath) {
      navigate(targetPath);
    }
  };

  return (
    <motion.div
      className="max-w-6xl mx-auto px-2 md:px-6 py-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <ApparelHeader />
      <div className="mt-6">
        <motion.div
          className="rounded-2xl shadow-xl border border-gray-100/40 bg-white/80 backdrop-blur-md p-0"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="flex w-full justify-center bg-gradient-to-r from-slate-50 via-white to-slate-50 p-1.5 rounded-2xl mb-6 shadow-lg border border-slate-200/60 backdrop-blur-sm">
              <TabsTrigger 
                value="shop" 
                className="relative rounded-xl px-8 py-3 text-sm font-medium transition-all duration-300 text-slate-600 hover:text-slate-800 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-xl data-[state=active]:shadow-blue-500/25 data-[state=active]:scale-105 hover:bg-slate-50 flex items-center gap-2"
                onClick={() => handleTabNavigation('apparel')}
              >
                <span className="relative z-10">Shop</span>
                {cartItemCount > 0 && (
                  <span className="relative z-10 bg-white/90 backdrop-blur-sm text-blue-600 rounded-full px-2 py-0.5 text-xs font-bold shadow-sm border border-blue-200/50">
                    {cartItemCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="manage" 
                className="relative rounded-xl px-8 py-3 text-sm font-medium transition-all duration-300 text-slate-600 hover:text-slate-800 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-xl data-[state=active]:shadow-blue-500/25 data-[state=active]:scale-105 hover:bg-slate-50"
                onClick={() => handleTabNavigation('apparel/manage')}
              >
                <span className="relative z-10">Manage Catalog</span>
              </TabsTrigger>
              <TabsTrigger 
                value="orders" 
                className="relative rounded-xl px-8 py-3 text-sm font-medium transition-all duration-300 text-slate-600 hover:text-slate-800 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-xl data-[state=active]:shadow-blue-500/25 data-[state=active]:scale-105 hover:bg-slate-50"
                onClick={() => handleTabNavigation('apparel/orders')}
              >
                <span className="relative z-10">Orders</span>
              </TabsTrigger>
            </TabsList>
            <div className="p-6 md:p-12 lg:p-16">
              <AnimatePresence mode="wait">
                {activeTab === "shop" && (
                  <motion.div
                    key="shop"
                    variants={tabVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    <ApparelShop />
                  </motion.div>
                )}
                {activeTab === "manage" && (
                  <motion.div
                    key="manage"
                    variants={tabVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    <ApparelManagement />
                  </motion.div>
                )}
                {activeTab === "orders" && (
                  <motion.div
                    key="orders"
                    variants={tabVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    <ApparelOrders />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </Tabs>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Apparel;
